import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogDescriptionParagraph,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { AgreementRenderer } from '@/features/account/components/AgreementRenderer'
import { TextField } from '@/components/inputs/TextField'
import {
  FormField,
  FormFieldLabel,
  FormFieldHint,
  FormFieldGroup,
} from '@/components/Form'
import { dateFormatters } from '@/utils/date-util'
import {
  SignatureDateLabel,
  SignatureSection,
} from '@/features/account/section-7216/components/SignatureSection'

export interface Section7216AcceptanceDialogProps {
  isOpen: boolean
  isLoading?: boolean
  markup: string
  userSignature: string
  spouseSignature: string
  spouseRequired: boolean
  userSignatureError?: string
  spouseSignatureError?: string
  onUserSignatureChange: (value: string) => void
  onSpouseSignatureChange: (value: string) => void
  onAccept: () => void
  onDecline: () => void
  isAcceptPending?: boolean
  testId?: string
  userSignatureInputTestId?: string
  spouseSignatureInputTestId?: string
  consentButtonTestId?: string
  closeButtonTestId?: string
}

export function Section7216AcceptanceDialog({
  isOpen,
  isLoading = false,
  markup,
  userSignature,
  spouseSignature,
  spouseRequired,
  userSignatureError,
  spouseSignatureError,
  onUserSignatureChange,
  onSpouseSignatureChange,
  onAccept,
  onDecline,
  isAcceptPending = false,
  testId,
  userSignatureInputTestId,
  spouseSignatureInputTestId,
  consentButtonTestId,
  closeButtonTestId,
}: Section7216AcceptanceDialogProps) {
  const isDisabled =
    isLoading || !userSignature || (spouseRequired && !spouseSignature)

  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onDecline}
      aria-label="Section 7216 Authorization"
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Consent needed to proceed with your return
        </ModalDialogTitle>
        <ModalDialogDescription>
          <ModalDialogDescriptionParagraph>
            Please review the disclosure details and provide your consent below
          </ModalDialogDescriptionParagraph>
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <AgreementRenderer
          isSkeleton={isLoading}
          markup={markup}
          withBackground
        />
        <SignatureSection>
          <FormFieldGroup stackWhenCompact>
            <FormField>
              <FormFieldLabel>Your full name</FormFieldLabel>
              <TextField
                value={userSignature}
                onChange={onUserSignatureChange}
                placeholder="Type your full name"
                invalid={!!userSignatureError}
                testId={userSignatureInputTestId}
              />
              <FormFieldHint invalid={!!userSignatureError}>
                {userSignatureError}
              </FormFieldHint>
            </FormField>

            {spouseRequired && (
              <FormField>
                <FormFieldLabel>Spouse's full name</FormFieldLabel>
                <TextField
                  value={spouseSignature}
                  onChange={onSpouseSignatureChange}
                  placeholder="Type spouse's full name"
                  invalid={!!spouseSignatureError}
                  testId={spouseSignatureInputTestId}
                />
                <FormFieldHint invalid={!!spouseSignatureError}>
                  {spouseSignatureError}
                </FormFieldHint>
              </FormField>
            )}
          </FormFieldGroup>
          <SignatureDateLabel>
            Signed on {dateFormatters.dateAmerican.format(new Date())}
          </SignatureDateLabel>
        </SignatureSection>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Consent & continue"
            onPress={onAccept}
            isDisabled={isDisabled}
            isPending={isAcceptPending}
            testId={consentButtonTestId}
          />
          <Button
            label="Close"
            variant="outline"
            onPress={onDecline}
            isDisabled={isAcceptPending || isLoading}
            testId={closeButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
